import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { Entreprise, User } from './user.model';
import { ALL_ASSISTANT_CHAT_CONFIGS } from '@features/assistant/models/assistant-chat-config';

// Ré-exports historiques : conservés pour ne pas casser les imports existants
// (`import { User } from '@core/auth/auth.service'`). Le code neuf doit
// préférer importer directement depuis `@core/auth/user.model`.
export type { Entreprise, User } from './user.model';

export interface LoginRequest {
  login: string;
  motDePasse: string;
}

export interface VerifyOtpRequest {
  otpSessionId: string;
  code: string;
  trustDevice?: boolean;
  deviceName?: string;
}

export interface VerifyOtpResponse {
  token: string;
  user: User;
}

export interface LoginResponse {
  success: boolean;
  token: string | null;
  email: string;
  expiresAt: Date;
  user: User | null;
  errorMessage?: string;
  // Champs 2FA
  requiresOtp?: boolean;
  otpSessionId?: string;
}

export interface ChangePasswordRequest {
  userId: string;
  ancienMotDePasse: string;
  nouveauMotDePasse: string;
}

export interface ChangePasswordDirectRequest {
  login: string;
  ancienMotDePasse: string;
  nouveauMotDePasse: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  /** Session OTP en attente (stocké temporairement entre les étapes login et verify-otp) */
  public pendingOtpSessionId: string | null = null;

  constructor(private http: HttpClient) {
    // Charger l'utilisateur depuis le localStorage au démarrage
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        // Ne charger que si le token est encore valide
        if (!this.isTokenExpired()) {
          this.currentUserSubject.next(JSON.parse(storedUser));
        } else {
          localStorage.removeItem('currentUser');
          localStorage.removeItem('authToken');
        }
      } catch (e) {
        // Si le parsing échoue, on nettoie le localStorage
        localStorage.removeItem('currentUser');
        localStorage.removeItem('authToken');
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    // withCredentials: indispensable pour transmettre/recevoir le cookie d'appareil de confiance (td_token)
    return this.http.post<LoginResponse>(`${this.apiUrl}/api/auth/login`, credentials, { withCredentials: true })
      .pipe(
        tap(response => {
          if (response.requiresOtp) {
            this.pendingOtpSessionId = response.otpSessionId || null;
          } else if (response.token && response.user) {
            localStorage.setItem('authToken', response.token);
            localStorage.setItem('currentUser', JSON.stringify(response.user));
            this.currentUserSubject.next(response.user);
          }
        })
      );
  }

  verifyOtp(otpSessionId: string, code: string, trustDevice: boolean = false, deviceName?: string): Observable<VerifyOtpResponse> {
    const payload: VerifyOtpRequest = { otpSessionId, code, trustDevice, deviceName };
    return this.http.post<VerifyOtpResponse>(`${this.apiUrl}/api/auth/verify-otp`, payload, { withCredentials: true })
      .pipe(
        tap(response => {
          this.pendingOtpSessionId = null;
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('currentUser', JSON.stringify(response.user));
          this.currentUserSubject.next(response.user);
        })
      );
  }

  enableTwoFactor(userId?: number): Observable<any> {
    const params = userId ? `?userId=${userId}` : '';
    return this.http.post(`${this.apiUrl}/api/auth/2fa/enable${params}`, {});
  }

  disableTwoFactor(userId?: number): Observable<any> {
    const params = userId ? `?userId=${userId}` : '';
    return this.http.post(`${this.apiUrl}/api/auth/2fa/disable${params}`, {});
  }

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    // Conversations en cours des assistants (identifiants de conversation et de réponse) :
    // effacées pour qu'un autre utilisateur du même poste ne reprenne pas le fil précédent.
    for (const config of ALL_ASSISTANT_CHAT_CONFIGS) {
      localStorage.removeItem(config.threadStorageKey);
      localStorage.removeItem(config.conversationStorageKey);
    }
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  isTokenExpired(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return true;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Décodage base64url robuste aux caractères Unicode dans les claims
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '==='.slice((base64.length + 3) % 4);
      const binary = atob(padded);
      const json = decodeURIComponent(
        binary
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(json);

      // Un token sans claim 'exp' est considéré comme invalide (sécurité par défaut)
      if (typeof decoded.exp !== 'number') return true;

      return decoded.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  changerMotDePasse(requete: ChangePasswordRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/changer-mot-de-passe`, requete);
  }

  changerMotDePasseDirect(requete: ChangePasswordDirectRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/changer-mot-de-passe-direct`, requete);
  }

  forgotPassword(email: string): Observable<any> {
    const clientUrl = window.location.origin;
    return this.http.post(`${this.apiUrl}/api/auth/forgot-password`, { email, clientUrl });
  }

  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/reset-password`, { token, newPassword });
  }

  updateCurrentUserTwoFactor(enabled: boolean): void {
    const user = this.currentUserSubject.value;
    if (user) {
      const updated = { ...user, twoFactorEnabled: enabled };
      localStorage.setItem('currentUser', JSON.stringify(updated));
      this.currentUserSubject.next(updated);
    }
  }
}